import React, { createContext } from 'react';
import loadable from '@loadable/component';
import { Navigate, Route, Routes } from 'react-router';
import { NotFound } from '@loadable-elements/NotFound';
import { SiteStructureType } from '../../../../types/siteStructureType';
import EventPage from './pages/EventPage/EventPage';
import Events from './pages/Events';
import LocalArticle from './pages/LocalArticle/LocalArticle';
import LocalBenefitArticle from './pages/LocalBenefitArticle/LocalBenefitArticle';
import LocalDepartment from './pages/LocalDepartment/LocalDepartment';
import LocalDepartments from './pages/LocalDepartments';
import MCClubs from './pages/MCClubs';
import NafCenters from './pages/NafCenters/NafCenters';
import PracticeTracks from './pages/PracticeTracks';
import { NafLocationSlugs, useHandleNafLocationsRoutes } from './useHandleNafLocationsRoutes';

const PracticeTrack = loadable(() => import('./pages/PracticeTrack/PracticeTrack'));

export const NafLocationsAppContext = createContext<SiteStructureType>(null);

// Descendant <Routes> mount under the app's root catch-all, so paths are matched relative
// to "/". Strip the leading slash from the absolute site-structure URLs.
const rel = (path: string) => path.replace(/^\//, '');

const NafLocationsApp = ({ data }: { data?: SiteStructureType }) => {
  const { locations } = useHandleNafLocationsRoutes();

  if (!data) return <NotFound />;
  const nafLocationPath = rel(data.slug);
  const localDepartmentUrl = rel(locations[NafLocationSlugs.localDepartment].fullUrl);
  const mcClubsUrl = rel(locations[NafLocationSlugs.mcClubs].fullUrl);
  const nafCenterUrl = rel(locations[NafLocationSlugs.nafCenter].fullUrl);
  const practiceTracksUrl = rel(locations[NafLocationSlugs.practiceTracks].fullUrl);
  const eventsUrl = rel(locations[NafLocationSlugs.events].fullUrl);

  return (
    <NafLocationsAppContext.Provider value={data}>
      <Routes>
        {/* the base path has no page of its own, so default to the local department listing */}
        <Route
          path={nafLocationPath}
          element={<Navigate replace to={locations[NafLocationSlugs.localDepartment].fullUrl} />}
        />

        {/* local department routes */}
        <Route path={localDepartmentUrl} element={<LocalDepartments />} />
        <Route path={`${localDepartmentUrl}/:slug`} element={<LocalDepartment />} />
        <Route path={`${localDepartmentUrl}/:lokalSlug/fordeler/:slug`} element={<LocalBenefitArticle />} />
        <Route path={`${localDepartmentUrl}/:lokalSlug/artikkel/:slug`} element={<LocalArticle />} />

        {/* Mc club routes */}
        <Route path={mcClubsUrl} element={<MCClubs />} />
        <Route path={`${mcClubsUrl}/:slug`} element={<LocalDepartment />} />
        <Route path={`${mcClubsUrl}/:lokalSlug/fordeler/:slug`} element={<LocalBenefitArticle />} />
        <Route path={`${mcClubsUrl}/:lokalSlug/artikkel/:slug`} element={<LocalArticle />} />

        {/* NAF center routes */}
        <Route path={nafCenterUrl} element={<NafCenters />} />

        {/* Practice track routes */}
        <Route path={practiceTracksUrl} element={<PracticeTracks />} />
        <Route path={`${practiceTracksUrl}/:slug`} element={<PracticeTrack />} />

        {/* Event routes */}
        <Route path={eventsUrl} element={<Events />} />
        <Route path={`${eventsUrl}/:slug`} element={<EventPage />} />
      </Routes>
    </NafLocationsAppContext.Provider>
  );
};

export default NafLocationsApp;
